/**
 * ELEMNT — Puente compartido con el `variant-picker` nativo.
 * ---------------------------------------------------------------------------------
 * Extraído durante la fase Talla (2026-08-27) a partir del patrón que ya probó
 * `assets/elemnt-fit-guide.js`. `elemnt-size-guide.js` lo usa desde el día uno.
 *
 * 🔴 Por qué existe este archivo y por qué `elemnt-fit-guide.js` NO se tocó para usarlo.
 *
 * El encargo de esta tanda es explícito: «Preserve the existing Fit/Horma implementation»
 * y «Do not refactor unrelated systems during this batch». `elemnt-fit-guide.js` ya está
 * implementado y validado localmente — moverle el suelo por debajo para que importe de
 * aquí es exactamente el tipo de refactor que el encargo pide NO hacer en esta tanda. Así
 * que hay una duplicación deliberada y temporal: la Guía de Tallas usa este módulo nuevo;
 * la Guía de Hormas conserva sus copias locales de las mismas tres funciones. Migrar
 * `elemnt-fit-guide.js` a este módulo compartido es un cambio seguro y de bajo riesgo para
 * una tanda futura dedicada a limpieza — no antes.
 *
 * Las tres funciones son genéricas a propósito: ninguna conoce "Horma" ni "Talla", ninguna
 * compara contra un nombre de opción de Shopify. Reciben el conjunto de VALORES exactos
 * que le importan a quien llama (`data-elemnt-fit-action`, `data-elemnt-size-action`, lo
 * que sea) y hacen coincidencia exacta contra `radio.value` — mismo criterio que ya usa
 * `product-media-gallery-content.liquid` para Color Gallery y que `elemnt-fit-guide.js`
 * usa para Horma: cero `if option.name === '…'`.
 */

/** El `<variant-picker>` de la PDP real — nunca el de quick-add ni el de product-card. */
export const PICKER_SELECTOR = 'variant-picker[data-template-product-match="true"]';

/** El radio cuyo `value` coincide EXACTO con `value`. */
export function findOptionRadio(picker, value) {
  const radios = picker.querySelectorAll('input[type="radio"]');

  for (const radio of radios) {
    if (radio.value === value) return radio;
  }

  return null;
}

/**
 * El `<fieldset>` que contiene un radio cuyo valor coincide con alguno de `values` — el
 * fieldset de una opción completa (Horma, Talla…), localizado por sus valores reales, no
 * por su nombre.
 */
export function findOptionFieldset(picker, values) {
  for (const value of values) {
    const radio = findOptionRadio(picker, value);
    if (radio) return radio.closest('fieldset');
  }

  return null;
}

/**
 * Reubica `trigger` (ya renderizado, nunca clonado) DENTRO del `<legend>` del fieldset
 * localizado por `values`, como su último hijo. Si el fieldset todavía no existe —primer
 * render antes de que el picker esté en el DOM, o un producto sin esa opción— no hace nada
 * y el disparador se queda donde Liquid lo puso originalmente.
 *
 * 2026-08-28 — vive DENTRO del legend, no como hermano tras él: `<legend>` queda excluido
 * del formatting context (flex/grid) del fieldset por especificación HTML, así que un
 * disparador hermano nunca puede alinearse junto al título en una sola fila. Como hijo del
 * legend, el CSS de quien llama puede volver el legend un flex row (nombre de la opción a
 * la izquierda, disparador a la derecha) con `legend:has(> .clase-del-disparador)`.
 *
 * Devuelve `true` si reubicó (o ya estaba en su sitio), `false` si no encontró dónde.
 */
export function relocateTrigger(trigger, picker, values, modifierClass) {
  if (!trigger || !picker || !values.length) return false;

  const fieldset = findOptionFieldset(picker, values);
  const legend = fieldset?.querySelector('legend');
  if (!legend) return false;

  if (trigger.parentElement !== legend) {
    legend.append(trigger);
  }

  if (modifierClass) trigger.classList.add(modifierClass);

  return true;
}
